import React, { useEffect, useRef, useState } from 'react';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import clsx from 'clsx';
import { parseKeybinding, tinykeys } from 'tinykeys';

function keyboardShortcutToString(modifiers: string[], key: string) {
	if (navigator.platform.startsWith('Mac')) {
		const symbols = { 'Meta': '⌘', 'Control': '⌃', 'Shift': '⇧', 'Alt': '⌥' };
		return `${modifiers.map((m) => symbols[m]).join()}${key}`;
	} else if (navigator.platform.startsWith('Win') || navigator.platform.startsWith('Linux')) {
		const symbols = { 'Meta': 'Ctrl', 'Control': 'Ctrl', 'Shift': 'Shift', 'Alt': 'Alt' };
		return `${modifiers.map((m) => symbols[m]).join('+')}+${key}`;
	} else {
		return null;
	}
}

export type SearchBarProps = {
	placeholder?: string;
	hasResults?: boolean;
	selectOnFocus?: boolean;
	onChange?: (value: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	icon?: React.ReactNode | null;
	value?: string;
	// tinykeys keybinding, ex: '$mod+P' for Ctrl+P on Windows and Cmd+P on Mac
	// https://github.com/jamiebuilds/tinykeys
	shortcut?: string;
	className?: React.HTMLProps<HTMLInputElement>['className'];
};

export function SearchBar({
	placeholder,
	hasResults = false,
	selectOnFocus = true,
	onChange,
	onFocus,
	onBlur,
	icon,
	value,
	className,
	shortcut,
} : SearchBarProps) {
	const input = useRef<HTMLInputElement>(null);
	const [hasValue, setHasValue] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		if (!shortcut) {
			return () => {};
		}

		const unsubscribe = tinykeys(window, {
			[shortcut]: (e) => {
				e.preventDefault();
				input.current?.focus();
			},
		});

		return unsubscribe;
	}, [shortcut]);

	useEffect(() => {
		setHasValue(!!value);
	}, [value]);

	const handleClickToFocus = (e: React.MouseEvent) => {
		input.current?.focus()
	}

	const handleInputFocus = (e: React.FocusEvent) => {
		setIsFocused(true);
		onFocus?.();
		if (selectOnFocus) {
			setTimeout(() => {
				input.current?.select()
			}, 0);
		}
	}

	const handleInputBlur = (e: React.FocusEvent) => {
		setIsFocused(false);
		onBlur?.();
	}

	const handleOnChange = (e) => {
		setHasValue(!!e.target.value)
		onChange?.(e.target.value)
	}

	const handleClear = () => {
		input.current.value = ''
		setHasValue(false)
		onChange?.('')
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			input.current?.blur();
		}
	};

	return (
		<div
			className={clsx(
				"flex cursor-text items-center gap-4 rounded-[20px] border px-4 py-3 transition",
				"border-borders-primary bg-bg-static-surface_default hover:border-borders-primary_hover",
				// Inner input state style
				"focus-within:border-borders-primary_pressed! focus-within:bg-bg-static-surface_default!",
				// Remain styled like focus while has value
				hasValue && "border-borders-primary_pressed! bg-bg-static-surface_default!",
				hasResults && "rounded-b-none!",
				className,
			)}
			onClick={handleClickToFocus}
		>
			{icon}
			{!icon && icon !== null && (
				<SearchIcon className="text-fg-primary text-2xl" />
			)}
			<input
				ref={input}
				type="text"
				placeholder={placeholder}
				className="w-full bg-transparent outline-hidden text-grey-800 placeholder-text-tertiary focus:placeholder-opacity-0 font-normal text-sm"
				onFocus={handleInputFocus}
				onBlur={handleInputBlur}
				onChange={handleOnChange}
				onKeyDown={handleKeyDown}
				value={value}
				tabIndex={0}
				autoComplete="off"
			/>
			{(hasValue || isFocused) && (
				<button
					type="button"
					className="closeButton text-fg-primary hover:text-fg-primary leading-none"
					onClick={handleClear}
					tabIndex={-1}
				>
					<CloseIcon className="closeIcon inline-block fill-current size-[22px]!" />
				</button>
			)}
			{shortcut && !hasValue && !isFocused && (
				<div className="hidden sm:flex items-center rounded-md border border-grey-100 px-1 py-[1px] text-xs font-normal tracking-wide text-grey-800">
					{keyboardShortcutToString(...parseKeybinding(shortcut)[0])}
				</div>
			)}
		</div>
	);
}
